import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getAccountingEntryClasses,
  getAccountingEntryTypes,
  getAccountingNodes,
  getAccountingPeriods,
} from '../../../../../../../../lib/api/accounting';
import CustomAlert from '../../../../../../../../lib/designSystem/Alert';
import {
  formatDate,
  formatMoney,
} from '../../../../../../../../lib/functions/utils';
import { RootState } from '../../../../../../../../services/redux/store';
import {
  AccountingEntryClass,
  AccountingNode,
  AccountingPeriod,
  EntryType,
} from '../../../../types';
import EntryWithLines from '../../../AccountingEntries/components/EntryWithLines/EditEntry';
import EntryModal from '../../../BookEntries/components/Modal';

const useStyles = makeStyles(() =>
  createStyles({
    tableCell: {
      padding: '8px !important',
      overflow: 'hidden',
      minWidth: '50px',
      maxWidth: '180px',
      whiteSpace: 'nowrap',
      fontSize: '14px !important',
    },
    clickableRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
  }),
);

interface GroupedLine {
  entryId?: string;
  className?: string;
  date: string;
  description?: string;
  auxiliaryBusinessIdentifier?: string;
  businessIdentifier?: string;
  debit?: number;
  credit?: number;
  debitFinal?: number;
  creditFinal?: number;
  accountName?: string;
  account?: string;
}

interface MovementsTableProps {
  groupedLines: GroupedLine[];
  groupedByCategoryAndBusiness: Record<string, Record<string, any[]>>;
  refreshData?: (showLoading?: boolean) => Promise<void>;
}

const MovementsTable = ({
  groupedLines,
  groupedByCategoryAndBusiness,
  refreshData,
}: MovementsTableProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [entryWithLinesOpen, setEntryWithLinesOpen] = useState(false);
  const [entryForEditing, setEntryForEditing] = useState<any>(null);
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [nodes, setNodes] = useState<AccountingNode[]>([]);
  const [entryClasses, setEntryClasses] = useState<AccountingEntryClass[]>([]);
  const [accountingPeriods, setAccountingPeriods] = useState<
    AccountingPeriod[]
  >([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>(
    'error',
  );
  const user = useSelector((state: RootState) => state.auth.user);

  const handleRowClick = (row: any) => {
    const foundGroup = findGroupByMovementKey(row);
    setSelectedCategory(foundGroup);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCategory(null);
  };

  const setUpEntryEditing = async () => {
    try {
      const response = await getAccountingEntryTypes(dispatch);
      const responseNodes = await getAccountingNodes(dispatch);
      const responseEntryClasses = await getAccountingEntryClasses(dispatch);
      const responsePeriods = await getAccountingPeriods(dispatch);

      setEntryTypes(response);
      setNodes(responseNodes);
      setEntryClasses(responseEntryClasses);
      setAccountingPeriods(responsePeriods);
    } catch (error) {
      console.error('Error fetching entry editing data:', error);
    }
  };

  const setUp = async (_entryId?: number) => {
    if (refreshData) {
      await refreshData(false);
    }
    setEntryWithLinesOpen(false);
    setEntryForEditing(null);
  };

  const handleSetSuccessText = (text: string | null) => {
    if (text) {
      setAlertText(text);
      setAlertSeverity('success');
      setAlertOpen(true);
    }
  };

  const handleSetFailedText = (text: string | null) => {
    if (text) {
      setAlertText(text);
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  const handleAlertClose = (open: boolean) => {
    setAlertOpen(open);
  };

  const findGroupByMovementKey = (
    row: any,
  ): {
    date: string;
    category: string;
    groupedLines: any[];
    businessName: string;
    businessIdentifier: string;
  } | null => {
    const formattedDate = formatDate(row.date, 'yyyy-mm-dd');
    const dateGroups = groupedByCategoryAndBusiness?.[formattedDate];
    if (!dateGroups) {
      return null;
    }
    const allEntries = Object.values(dateGroups).flat();
    const matchingEntry = allEntries.find(
      (entry: any) => entry.movementKey === row.movementKey,
    );
    if (matchingEntry) {
      const categoryAndAccount = Object.keys(dateGroups).find((key) =>
        dateGroups[key].some(
          (entry: any) => entry.movementKey === row.movementKey,
        ),
      );
      return {
        date: formattedDate,
        category: matchingEntry.category,
        groupedLines: dateGroups[categoryAndAccount!],
        businessName: matchingEntry.businessName,
        businessIdentifier: matchingEntry.businessIdentifier,
      };
    }
    return null;
  };

  const handleExportToExcel = async () => {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Libro Mayor');

    const accountName = groupedLines[0]?.accountName || 'Cuenta';
    const accountCode = groupedLines[0]?.account || '';

    // Extraer fechas mínima y máxima de los datos
    const dates = groupedLines
      .map((line: any) => new Date(line.date))
      .filter((date: Date) => !isNaN(date.getTime()));
    const startDate =
      dates.length > 0
        ? new Date(Math.min(...dates.map((d: Date) => d.getTime())))
        : undefined;
    const endDate =
      dates.length > 0
        ? new Date(Math.max(...dates.map((d: Date) => d.getTime())))
        : undefined;

    // Filas de información
    const infoRows = [
      ['Informacion de la consulta', ''],
      ['Razón social:', user?.companyName || 'N/A'],
      ['RUT:', user?.businessIdentifier || 'N/A'],
      ['Tipo de balance:', 'Libro Mayor'],
      [
        'Periodo:',
        startDate && endDate
          ? `${formatDate(startDate)} a ${formatDate(endDate)}`
          : 'N/A',
      ],
      ['Cuenta:', `${accountCode} - ${accountName}`],
      ['Fecha de consulta:', formatDate(new Date())],
      [], // Fila vacía como separador
    ];

    // Agregar filas de información
    infoRows.forEach((rowData) => {
      const row = worksheet.addRow(rowData);
      // Formato para las filas de información (primera columna en negrita)
      if (rowData.length > 0) {
        const firstCell = row.getCell(1);
        firstCell.font = { bold: true };
      }
    });

    // Agregar título
    const titleRow = worksheet.addRow([accountName]);
    titleRow.font = { bold: true, size: 14 };
    const titleRowNumber = worksheet.rowCount;
    worksheet.mergeCells(titleRowNumber, 1, titleRowNumber, 7);
    worksheet.addRow([]);

    // Headers
    const headers = [
      'ID Asiento',
      'Clase Asiento',
      'Fecha',
      'Descripción',
      'Rut',
      'Debe',
      'Haber',
      'Débito',
      'Crédito',
    ];
    const headerRow = worksheet.addRow(headers);

    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDDDDDD' },
      };
    });

    // Configurar ancho de columnas
    worksheet.getColumn(1).width = 12; // Fecha
    worksheet.getColumn(2).width = 40; // Descripción
    worksheet.getColumn(3).width = 12; // Rut
    worksheet.getColumn(4).width = 15; // Debe
    worksheet.getColumn(4).width = 15; // Haber
    worksheet.getColumn(5).width = 15; // Débito
    worksheet.getColumn(6).width = 15; // Crédito

    // Configurar formato de números para las columnas de montos
    [3, 4, 5, 6].forEach((col) => {
      worksheet.getColumn(col).numFmt = '#,##0';
    });

    // Agregar datos
    groupedLines.forEach((line: any) => {
      worksheet.addRow([
        line.entryId,
        line.className,
        formatDate(line.date),
        line.description || '',
        line.auxiliaryBusinessIdentifier || line.businessIdentifier,
        line.debit || 0,
        line.credit || 0,
        line.debitFinal || 0,
        line.creditFinal || 0,
      ]);
    });

    // Agregar fila de totales
    const totalsRow = worksheet.addRow([
      'Totales:',
      '',
      '',
      '',
      '',
      groupedLines.reduce(
        (sum: number, line: any) => sum + (line.debit || 0),
        0,
      ),
      groupedLines.reduce(
        (sum: number, line: any) => sum + (line.credit || 0),
        0,
      ),
      groupedLines.reduce(
        (sum: number, line: any) => sum + (line.debitFinal || 0),
        0,
      ),
      groupedLines.reduce(
        (sum: number, line: any) => sum + (line.creditFinal || 0),
        0,
      ),
    ]);

    totalsRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6E6' },
      };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'double' },
      };
    });

    // Guardar el archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `libro_mayor_${accountName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();

    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    void setUpEntryEditing();
  }, []);

  const handleEditEntry = (entry: any) => {
    // Get the first line to extract common entry data
    const firstLine = entry.groupedLines?.[0];
    if (!firstLine) {
      console.error('No lines found in entry');
      return;
    }

    // Try to find classId from entry or first line
    // If className exists, we might need to find the matching classId from entryClasses
    let classId = entry.classId || firstLine.classId || null;
    if (!classId && firstLine.className && entryClasses.length > 0) {
      // Try to find classId by matching className
      const matchingClass = entryClasses.find(
        (ec: AccountingEntryClass) => ec.name === firstLine.className,
      );
      if (matchingClass) {
        classId = matchingClass.id;
      }
    }

    const entryForEdit = {
      id: firstLine.entryId || null,
      name: entry.category || entry.name || '',
      typeId: entry.typeId || 'free',
      classId: classId,
      date:
        entry.date || firstLine.date || new Date().toISOString().slice(0, 10),
      sourceKey: entry.sourceKey || firstLine.entryId || null,
      status: entry.status || 'CLOSED',
      companyId: user?.companyId || 0,
      lines:
        entry.groupedLines?.map((line: any) => ({
          id: line.id || null,
          nodeId: line.nodeId || null,
          debit: line.debit || 0,
          credit: line.credit || 0,
          costCenterId: line.costCenterId || null,
          documentKey: line.documentKey || '',
          businessIdentifier:
            line.businessIdentifier || line.auxiliaryBusinessIdentifier || '',
          description: line.description || '',
          node: line.node || null,
        })) || [],
    };

    setEntryForEditing(entryForEdit);
    setEntryWithLinesOpen(true);
    setModalOpen(false);
  };

  return (
    <>
      <Paper elevation={0} style={{ padding: 16, margin: '12px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 8,
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {groupedLines[0].accountName}
          </Typography>
          <IconButton
            onClick={() => {
              void handleExportToExcel();
            }}
            size="small"
            color="primary"
            title="Exportar a Excel"
          >
            <FileDownloadIcon />
          </IconButton>
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID Asiento</TableCell>
              <TableCell>Clase</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Debe</TableCell>
              <TableCell>Haber</TableCell>
              <TableCell>Débito</TableCell>
              <TableCell>Crédito</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedLines.map((category: any, catIndex: number) => (
              <React.Fragment key={catIndex}>
                <TableRow
                  className={classes.clickableRow}
                  onClick={() => handleRowClick(category)}
                >
                  <TableCell className={classes.tableCell}>
                    {category.entryId}
                  </TableCell>
                  <TableCell>{category.className}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(category.date, 'dd-mm-yyyy')}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {category.description}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.debit)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.credit)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.debitFinal)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatMoney(category.creditFinal)}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            <TableRow>
              <TableCell
                colSpan={4}
                style={{
                  fontWeight: 'bold',
                  textAlign: 'left',
                }}
              >
                Totales:
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: GroupedLine) => sum + (line.debit || 0),
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: GroupedLine) =>
                      sum + (line.credit || 0),
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: GroupedLine) =>
                      sum + (line.debitFinal || 0),
                    0,
                  ),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  groupedLines.reduce(
                    (sum: number, line: GroupedLine) =>
                      sum + (line.creditFinal || 0),
                    0,
                  ),
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {selectedCategory && (
          <EntryModal
            open={modalOpen}
            onEdit={handleEditEntry}
            onClose={handleCloseModal}
            entry={selectedCategory}
          />
        )}
        {entryWithLinesOpen && entryForEditing && (
          <EntryWithLines
            entry={entryForEditing}
            setEntry={(updatedEntry: any) => {
              setEntryForEditing(updatedEntry);
            }}
            open={entryWithLinesOpen}
            setOpen={(open: boolean) => {
              setEntryWithLinesOpen(open);
            }}
            setUp={setUp}
            setSuccessText={handleSetSuccessText}
            setFailedText={handleSetFailedText}
            entryTypes={entryTypes}
            nodes={nodes}
            entryClasses={entryClasses}
            accountingPeriods={accountingPeriods}
          />
        )}
        <CustomAlert
          open={alertOpen}
          setOpen={handleAlertClose}
          severity={alertSeverity}
          title={alertSeverity === 'success' ? 'Éxito' : 'Error'}
          text={alertText}
        />
      </Paper>
    </>
  );
};

export default MovementsTable;
